import { useCallback, useEffect, useState } from "react";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";
import { Loader2, RefreshCw, BadgeCheck, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  interested: "bg-sky-100 text-sky-800",
  active: "bg-emerald-100 text-emerald-800",
};

function Status({ status }) {
  const Icon = status === "active" ? BadgeCheck : CheckCircle2;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_CLS[status] || "bg-neutral-200 text-neutral-700"}`}>
      <Icon className="h-3 w-3" /> {status === "active" ? "Active" : "Interested"}
    </span>
  );
}

function RegTable({ rows, testid, showStage }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500"
        data-testid={`${testid}-empty`}>
        No registrations yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white" data-testid={testid}>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <th className="px-4 py-3">Submitted</th>
            <th className="px-4 py-3">Founder</th>
            <th className="px-4 py-3">Company</th>
            <th className="px-4 py-3">{showStage ? "Business stage" : "Masterclass"}</th>
            <th className="px-4 py-3">CRS score</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reg_id} className="border-b border-neutral-100 align-top hover:bg-neutral-50/60"
              data-testid={`admin-masterclass-row-${r.reg_id}`}>
              <td className="whitespace-nowrap px-4 py-3 text-xs text-neutral-500">
                {(r.created_at || "").slice(0, 10)}
              </td>
              <td className="px-4 py-3">
                <p className="font-semibold text-neutral-900">{r.name || "—"}</p>
                <a href={`mailto:${r.email}`} className="text-xs text-[#0A8B85] hover:underline">{r.email}</a>
              </td>
              <td className="px-4 py-3 text-neutral-700">{r.company_name || "—"}</td>
              <td className="px-4 py-3 text-xs text-neutral-700">
                {showStage ? (r.business_stage || "—") : (r.kind_label || r.kind)}
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                {r.readiness_score != null ? (
                  <span className="font-bold text-neutral-900">{r.readiness_score}<span className="text-xs font-normal text-neutral-400"> / 100</span></span>
                ) : <span className="text-xs text-neutral-400">Not generated</span>}
              </td>
              <td className="whitespace-nowrap px-4 py-3"><Status status={r.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminMasterclasses() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/masterclass-registrations");
      setRows(data.registrations || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/masterclass-registrations.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clinq_masterclass_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Export failed. Try again.");
    }
  };

  const masterclassRows = rows.filter((r) => r.kind !== "crp");
  const programRows = rows.filter((r) => r.kind === "crp");

  return (
    <AdminShell current="/admin/masterclasses">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-100">Masterclasses &amp; programs</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Interest registrations for the Pitch Deck / Financial Model masterclasses and the
            Capital Readiness Program (ISBI Strathmore). Registrations flip to Active once the
            founder purchases a credits pack tagged to that masterclass.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="admin-masterclass-export-csv"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#066667]">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button onClick={load} data-testid="admin-masterclass-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-neutral-400">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <p className="inline-flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading registrations…
        </p>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-neutral-400">
              Masterclass registrations ({masterclassRows.length})
            </h2>
            <RegTable rows={masterclassRows} testid="admin-masterclass-table" showStage={false} />
          </section>
          <section>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-neutral-400">
              Program registrations — Capital Readiness Program ({programRows.length})
            </h2>
            <RegTable rows={programRows} testid="admin-program-table" showStage />
          </section>
        </div>
      )}
    </AdminShell>
  );
}
